<template>
  <Modal
    :modal-types="[WORKBENCH_PREFERENCES_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-cogs"
    modal-title="Workbench Preferences"
    :is-wide-modal="true"
  >
    <form class="workbench-preferences-form">
      <div class="workbench-preferences">
        <div>
          <SCThreeCheckbox
            id="workbench-preferences-modal-is-chart-center-aligned-checkbox"
            label-text="Center-Align Chart"
            description="Position your charts in the center of the screen"
            :checkbox-value="!isChartLeftAlignedLocalCopy"
            @update:checkbox-value="updateIsChartCenterAlignedLocalCopy"
            :is-dark-font="true"
            :icon-size="23"
            :title-font-size="18"
            :space-between-text-and-icon="8"
            :is-circle="true"
            :is-single-spaced="true"
            :is-bold="true"
          />
        </div>
        <div>
          <SCThreeCheckbox
            id="workbench-preferences-modal-is-chart-left-aligned-checkbox"
            label-text="Left-Align Chart"
            description="Position your charts against the left edge of the screen"
            v-model:checkbox-value="isChartLeftAlignedLocalCopy"
            :is-dark-font="true"
            :icon-size="23"
            :title-font-size="18"
            :space-between-text-and-icon="8"
            :is-circle="true"
            :is-single-spaced="true"
            :is-bold="true"
          />
        </div>
        <hr class="preference-divider">
        <div>
          <SCThreeCheckbox
            id="workbench-preferences-modal-auto-size-charts-checkbox"
            label-text="Auto-Size Charts"
            description="Scale charts to fit small screens automatically, or turn off to retain full size at all widths. Functionality is disabled while using inspector or annotation mode"
            v-model:checkbox-value="isAutoSizeChartsEnabledLocalCopy"
            :is-dark-font="true"
            :icon-size="23"
            :title-font-size="18"
            :space-between-text-and-icon="8"
            :is-toggle-slider="true"
            :is-single-spaced="true"
            :is-bold="true"
          />
        </div>
        <div>
          <SCThreeCheckbox
            id="workbench-preferences-modal-auto-refresh-checkbox"
            label-text="Auto-Refresh"
            description="Automatically refresh your chart's data"
            v-model:checkbox-value="isAutoRefreshEnabledLocalCopy"
            :is-dark-font="true"
            :icon-size="23"
            :title-font-size="18"
            :space-between-text-and-icon="8"
            :is-toggle-slider="true"
            :is-single-spaced="true"
            :is-bold="true"
          />
        </div>
      </div>
      <hr class="sc3-form-divider" />
      <button
        type="button"
        class="sc3-button sc3-button-green"
        @click="saveChanges()"
      >
        <i class="fas fa-fw fa-check-circle"></i>
        &nbsp;Save Preferences
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >Cancel</button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useChartDimensions } from '@/composables/useChartDimensions';
  import { useAutoRefresh } from '@/composables/useAutoRefresh';
  import { useModals } from '@/composables/useModals';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const { modalType } = storeToRefs(layoutStore);
  const { isLoggedIn } = useAuthentication();
  const {
    isChartLeftAligned,
    isAutoSizeChartsEnabled,
    setIsChartLeftAligned,
    setIsAutoSizeChartsEnabled
  } = useChartDimensions();
  const { isAutoRefreshEnabled, updateIsAutoRefreshEnabled } = useAutoRefresh();
  const { WORKBENCH_PREFERENCES_MODAL, launchCreateAccountModal, closeModal } = useModals();
  const isChartLeftAlignedLocalCopy = ref(isChartLeftAligned.value);
  const isAutoSizeChartsEnabledLocalCopy = ref(isAutoSizeChartsEnabled.value);
  const isAutoRefreshEnabledLocalCopy = ref(isAutoRefreshEnabled.value);

  function updateIsChartCenterAlignedLocalCopy(isChartCenterAligned: boolean) {
    isChartLeftAlignedLocalCopy.value = !isChartCenterAligned;
  }

  function saveChanges() {
    closeModal();
    setIsAutoSizeChartsEnabled(isAutoSizeChartsEnabledLocalCopy.value);

    if (
      !isLoggedIn.value &&
      (!isChartLeftAlignedLocalCopy.value || isAutoRefreshEnabledLocalCopy.value)
    ) {
      launchCreateAccountModal();
      return;
    }

    setIsChartLeftAligned(isChartLeftAlignedLocalCopy.value);
    updateIsAutoRefreshEnabled(isAutoRefreshEnabledLocalCopy.value);
  }

  function handleModalTypeChanged() {
    if (modalType.value !== WORKBENCH_PREFERENCES_MODAL) {
      return;
    }

    isChartLeftAlignedLocalCopy.value = isChartLeftAligned.value;
    isAutoSizeChartsEnabledLocalCopy.value = isAutoSizeChartsEnabled.value;
    isAutoRefreshEnabledLocalCopy.value = isAutoRefreshEnabled.value;
  }

  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .workbench-preferences-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .workbench-preferences {
    display: flex;
    flex-direction: column;
    gap: 20px;
    text-align: left;
  }

  hr.preference-divider {
    margin: 0px;
    border: 0;
    border-top: 3px solid #c0ced8;
    border-radius: 50px;
  }
</style>
